"use client";

import {
  useEffect,
  useRef,
  type ReactNode,
  type MouseEvent,
  type KeyboardEvent,
  type FC,
  type Ref,
} from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { rem, mergeClasses, mergeEvents } from "@huub-web/utils";

import { Card, type CardProps } from "../Card";
import { useMaybePubSub } from "../../hooks/usePubSub";
import { useUuid } from "../../hooks/useUuid";
import { useHover } from "../../hooks/useHover";
import { mergeRefs } from "../../utils/mergeRefs";
import { Flex } from "../Flex";
import { IconButton } from "../IconButton";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { Message } from "../Message";
import { Placeholder } from "../Placeholder";
import { Stack } from "../Stack";
import { Reveal } from "../Reveal";
import type { Timeout } from "../../types";
import { cardCollectionChildMotionVariants } from "../../config/cardCollectionMotion";
import {
  iconPopupVariants,
  iconPopupTransition,
} from "../../config/iconPopupMotion";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";

import {
  cardStyle,
  containerStyle,
  contentStyle,
  footerStyle,
  headerStyle,
  DEFAULT_CLOSED_CARD_WIDTH,
  CLOSED_CARD_HEIGHT,
  cardContentStyle,
  mainContentStyle,
  progressBarStyle,
  loadingContentStyle,
  subtitleStyle,
  expandedSubtitleStyle,
  heightAnimationStyle,
  collapsedContentStyle,
} from "./ExpandableCard.css";
import { ProgressBar } from "../ProgressBar";
import { theme } from "../../theme/css/theme.css";
import { Button } from "../Button";
import { useDebug } from "../../hooks";
import type { IconName } from "../../theme";

const PLACEHOLDER_HEIGHT = 42;

export type ExpandableCardProps = Omit<
  CardProps,
  "padding" | "title" | "width" | "children" | "content"
> & {
  /**
   * Whether the card can be expanded
   */
  expandable?: boolean;
  /**
   * Whether the card is in active state (animated gradient border)
   */
  active?: boolean;
  /**
   * Controls whether the card is expanded (fully controlled)
   */
  open?: boolean;
  /**
   * Title text for the header
   */
  title?: ReactNode;
  /**
   * Subtitle text displayed below the title
   */
  subtitle?: ReactNode;
  /**
   * Warning message shown as an alert icon with tooltip
   */
  warning?: string;
  /**
   * Whether the card is in a completed state (shows check icon)
   */
  completed?: boolean;
  /**
   * Whether the card is in a loading state
   */
  loading?: boolean;
  /**
   * Whether content has been loaded at least once.
   * Used to determine whether to show placeholders or dim existing content.
   */
  hasContent?: boolean;
  /**
   * Error message to display instead of content
   */
  error?: string | null;
  /**
   * Plain text label for ContentMenu registration.
   * Falls back to title if title is a string.
   */
  label?: string;
  /**
   * Icon for ContentMenu registration
   */
  menuIcon?: IconName;
  /**
   * Force position in ContentMenu
   */
  menuForcePosition?: "leading" | "leadingInside" | "trailing";
  /**
   * Text for the expand button
   */
  buttonValue?: ReactNode;
  /**
   * Override the button variant (defaults to "tertiary" when completed, "primary" otherwise)
   */
  buttonVariant?: "primary" | "tertiary";
  /**
   * Leading icon for the expand button
   */
  buttonIcon?: IconName;
  /**
   * Content to display when expanded and loaded
   */
  content?: ReactNode;
  /**
   * Callback when the card is opened
   */
  onOpen?: () => void;
  /**
   * Callback when the card is closed
   */
  onClose?: () => void;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * An expandable card component that displays a collapsed summary and an
 * expanded detail view. Handles loading states with placeholders.
 * Fully controlled via the `open` prop.
 */
export const ExpandableCard: FC<ExpandableCardProps> = ({
  id,
  expandable = false,
  active = false,
  title,
  subtitle,
  warning,
  completed,
  loading = false,
  hasContent = false,
  error,
  label,
  menuIcon,
  menuForcePosition,
  open = false,
  border = "default",
  disabled = false,
  overflowHidden = false,
  buttonValue,
  buttonVariant,
  buttonIcon,
  content,
  onOpen,
  onClose,
  onClick,
  className,
  ref,
  ...props
}) => {
  const { t } = useTranslation();
  const pubSub = useMaybePubSub();
  const uuid = useUuid();
  const { log } = useDebug();

  const { handleEnter, handleLeave } = useHover({ disabled });

  const derivedLabel = label ?? (typeof title === "string" ? title : "");

  const cardRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useContentMenuSection<HTMLDivElement>({
    id: id ?? "",
    label: derivedLabel,
    icon: menuIcon,
    forcePosition: menuForcePosition,
  });

  const resolvedButtonVariant =
    buttonVariant ?? (completed ? "tertiary" : "primary");

  const showPlaceholders = loading && !hasContent;
  const showLoadingOverlay = loading && hasContent;

  /**
   * When the card is not expandable but has an onOpen callback,
   * the entire card becomes a click-through target (e.g. for navigation).
   */
  const isClickThrough = !expandable && !disabled && !!onOpen;

  const handleExpandClick = () => {
    if (open) {
      return;
    }

    log("cyan", `ExpandableCard ${id}`, "handleExpandClick");

    if (cardRef.current) {
      cardRef.current.blur();
    }

    onOpen?.();
  };

  const handleCloseClick = () => {
    onClose?.();
  };

  const handleCardKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleExpandClick();
    }
  };

  useEffect(() => {
    if (!open || !uuid) {
      return;
    }

    const timeout: Timeout = setTimeout(() => {
      if (!containerRef.current) {
        return;
      }
      pubSub?.publish("scrollTo", { element: containerRef.current });
    }, 500);

    return () => clearTimeout(timeout);
  }, [open, uuid]);

  /**
   * Scroll carousel to top when reloading content
   */
  useEffect(() => {
    if (loading && hasContent && open) {
      pubSub?.publish("scrollToTop", { tag: "carousel" });
    }
  }, [loading, hasContent, open, pubSub]);

  const renderHeader = ({ expanded }: { expanded: boolean }) => {
    if (!title) {
      return null;
    }

    return (
      <Flex direction="column" gap="s">
        <Flex gap="s">
          <Text variant="displayS" clamp={2} bold grow>
            {title}
          </Text>
          {completed && (
            <motion.div
              variants={iconPopupVariants}
              initial="initial"
              animate="animate"
              transition={iconPopupTransition}
            >
              <Tooltip value={t("expandableCard.completedTooltip")}>
                <Icon icon="checkCircleFilled" color="gradient" />
              </Tooltip>
            </motion.div>
          )}
          {warning && (
            <motion.div
              variants={iconPopupVariants}
              initial="initial"
              animate="animate"
              transition={iconPopupTransition}
            >
              <Tooltip value={warning}>
                <Icon icon="alertFilled" color="warning">
                  {warning}
                </Icon>
              </Tooltip>
            </motion.div>
          )}
        </Flex>
        {subtitle && (
          <Text
            variant="bodyS"
            color="currentColor50"
            clamp={expanded ? undefined : 5}
            className={expanded ? expandedSubtitleStyle : subtitleStyle}
          >
            {subtitle}
          </Text>
        )}
      </Flex>
    );
  };

  const renderContent = () => {
    if (error) {
      return <Message variant="error">{error}</Message>;
    }

    return (
      <Stack>
        {/* Loading placeholders (initial load) */}
        <Reveal>
          {showPlaceholders && (
            <Flex gap="s" direction="column">
              <Placeholder height={rem(PLACEHOLDER_HEIGHT)} width="90%" />
              <Placeholder height={rem(PLACEHOLDER_HEIGHT)} width="50%" />
              <Placeholder height={rem(PLACEHOLDER_HEIGHT)} width="70%" />
              <Placeholder height={rem(PLACEHOLDER_HEIGHT)} width="30%" />
            </Flex>
          )}
        </Reveal>

        {/* Loaded content */}
        <Reveal renderAs="flex" gap="m">
          {hasContent && (
            <div
              className={loadingContentStyle({ loading: showLoadingOverlay })}
            >
              {content}
            </div>
          )}
        </Reveal>
      </Stack>
    );
  };

  return (
    <motion.div
      ref={mergeRefs(containerRef, sectionRef)}
      className={mergeClasses(containerStyle())}
      variants={cardCollectionChildMotionVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{
        width: open
          ? `calc(var(--container-width) - ${theme.spacings.m} * 2)`
          : DEFAULT_CLOSED_CARD_WIDTH,
      }}
      {...(active && { "data-active": true })}
    >
      <Card
        ref={mergeRefs(ref, cardRef)}
        {...props}
        id={id}
        disabled={disabled}
        className={mergeClasses(cardStyle, className)}
        contentClassName={cardContentStyle}
        border={
          open
            ? "gradient"
            : border !== "default"
              ? border
              : active
                ? "gradientAnimated"
                : border
        }
        direction="vertical"
        padding="none"
        borderRadius="s"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={
          isClickThrough ? mergeEvents(handleExpandClick, onClick) : undefined
        }
        onKeyDown={isClickThrough ? handleCardKeyDown : undefined}
        overflowHidden={overflowHidden}
      >
        {showLoadingOverlay && open && (
          <ProgressBar
            variant="infinite"
            position="absolute"
            data-testid="expandable-card-progress-bar"
            className={progressBarStyle}
          />
        )}

        <motion.div
          className={heightAnimationStyle}
          initial={false}
          animate={{
            height: open ? "auto" : CLOSED_CARD_HEIGHT,
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* Collapsed - absolutely positioned, fades */}
          <motion.div
            className={collapsedContentStyle}
            initial={false}
            animate={{ opacity: open ? 0 : 1 }}
            transition={{ duration: 0.2 }}
            style={{ pointerEvents: open ? "none" : "auto" }}
          >
            <Flex direction="column" className={mainContentStyle}>
              <Flex
                direction="column"
                className={headerStyle({ padding: true })}
              >
                {renderHeader({ expanded: false })}
              </Flex>

              {!!buttonValue && (
                <Flex
                  gap="xxs"
                  align="flex-end"
                  className={footerStyle({ padding: true })}
                >
                  <Button
                    disabled={open || disabled}
                    onClick={mergeEvents<MouseEvent<HTMLElement>>(
                      handleExpandClick,
                      onClick,
                    )}
                    variant={resolvedButtonVariant}
                    leading={
                      buttonIcon ? (
                        <Icon icon={buttonIcon} size="s" />
                      ) : undefined
                    }
                  >
                    {buttonValue}
                  </Button>
                </Flex>
              )}
            </Flex>
          </motion.div>

          {/* Expanded - in flow, controls height */}
          <div
            style={{
              visibility: open ? "visible" : "hidden",
              pointerEvents: open ? "auto" : "none",
            }}
          >
            <Flex ref={expandedRef} direction="column">
              <Flex
                direction="row"
                gap="xs"
                className={headerStyle({ padding: true })}
              >
                <Flex grow direction="column" gap="xs">
                  {renderHeader({ expanded: true })}
                </Flex>

                <IconButton
                  onClick={handleCloseClick}
                  size="xs"
                  icon="close"
                  variant="tertiary"
                  rounded
                />
              </Flex>

              <Flex
                direction="column"
                gap="xs"
                className={contentStyle({ padding: true })}
              >
                {renderContent()}
              </Flex>
            </Flex>
          </div>
        </motion.div>
      </Card>
    </motion.div>
  );
};
